import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MessageService } from '../message';
import { Task } from './task';

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private tasksSubject = new BehaviorSubject<Task[]>([]);

    /** Fonte única da verdade das tarefas em andamento/concluídas */
    tasks$: Observable<Task[]> = this.tasksSubject.asObservable();

    hasTasksExecuting: Observable<boolean> = this.tasks$.pipe(
        map((tasks) => tasks.some((task) => task.isExecuting))
    );

    constructor(private messageService: MessageService) {}

    /** Adiciona uma nova tarefa
     *
     * Quando a tarefa tem `key`, aplica deduplicação por identidade: se já existe
     * uma tarefa com a mesma chave em execução, o pedido é ignorado (retorna `false`);
     * se a tarefa existente já foi concluída, ela é substituída pela nova.
     *
     * @param messageExecuting é a mensagem exebida no momento em que a tarefa é adicionada
     * @returns `true` se a tarefa foi adicionada, `false` se foi ignorada por já estar em execução
     */
    add(task: Task, messageExecuting?: string): boolean {
        if (task.key !== undefined && task.key !== null) {
            const existing = this.tasksSubject.value.find((t) => t.key === task.key);
            if (existing?.isExecuting) {
                return false;
            }
            if (existing) {
                this.tasksSubject.next(
                    this.tasksSubject.value.filter((t) => t.key !== task.key)
                );
            }
        }

        task.isExecuting = true;
        this.tasksSubject.next([task, ...this.tasksSubject.value]);

        if (messageExecuting) {
            this.messageService.info(messageExecuting, null, {timeOut: 1000});
        } else {
            this.messageService.info(task.description, `Executando ${task.name}`, {timeOut: 1000});
        }

        return true;
    }

    /** Finaliza a tarefa com sucesso
     *
     * @param message mensagem que a tarefa foi executada com sucesso
     */
    success(task: Task, message: string) {
        task.messageSuccessful = message;
        task.successful = true;
        task.read = true;

        this.stop(task);
    }

    /** Finaliza a tarefa com erro
     *
     * @param title título do erro
     * @param message mensagem do erro
     * @param error o erro ocorrido
     */
    error(task: Task, title: string, message: string, error: any) {
        task.titleError = title;
        task.messageError = message;
        task.error = error;
        task.successful = false;
        task.read = false;

        this.stop(task);
    }

    /** Marca a tarefa como lida */
    markAsRead(task: Task) {
        task.read = true;
        this.emit();
    }

    /** Remove todas as tarefas */
    clearAll() {
        this.tasksSubject.next([]);
    }

    /** Para a execução da tarefa */
    private stop(task: Task) {
        task.isExecuting = false;
        task.finishedAt = new Date();

        this.emit();
    }

    /** Reemite o array atual com nova referência para acionar change detection OnPush */
    private emit() {
        this.tasksSubject.next([...this.tasksSubject.value]);
    }
}
